import {
  getCurrentFacilityId,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import { MedicalPaOrdersAuthStatus } from "../enums/medicalPaOrdersAuthStatus";
import { isExceptionFlowForUrologyOrg } from "../../shared/enums/organizationTypes";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/UserGeneralInfoStore";

/**
 * Gets the organization ID to use for external worklist API calls.
 * If the current facility is external, returns the corresponding internal org ID.
 * Otherwise, returns the current facility ID.
 * Reads from singleton store so it can be called from anywhere (including Redux thunks).
 */
export const getOrgIdForFetchExternalWorklist = (): string => {
  const currentFacilityId = getCurrentFacilityId() || "";
  const correspondingInternalOrgId = UserStore.getItem(
    UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
  );

  // If facility is external and has a corresponding internal org, use that
  if (
    correspondingInternalOrgId &&
    userLoggedInFacilityType(currentFacilityId) === "external"
  ) {
    return correspondingInternalOrgId;
  }

  return currentFacilityId;
};

/** Auth statuses that allow reassignment for urology exception-flow orgs. */
const SUNSTATE_REASSIGNMENT_STATUSES: ReadonlySet<string> = new Set([
  MedicalPaOrdersAuthStatus.AuthRequired,
  MedicalPaOrdersAuthStatus.Query,
  MedicalPaOrdersAuthStatus.QueryResolved,
]);

/** Auth statuses that allow reassignment for all other orgs. */
const DEFAULT_REASSIGNMENT_STATUSES: ReadonlySet<string> = new Set([
  MedicalPaOrdersAuthStatus.AuthRequired,
]);

/** Default BO confirmation prompt shown in the "Mark as Completed" modal. */
const DEFAULT_BO_CONFIRMATION_LABEL = "Confirm the global BO value :";

/** Label used when an exception-flow org surfaces the BO value as an "estimate". */
const SUNSTATE_BO_CONFIRMATION_LABEL = "Confirm the estimate value";

/**
 * Resolves the BO confirmation prompt label for the "Mark as Completed" modal.
 *
 * Orgs in `exceptionFlowForUrologyOrgs` present the BO value as an
 * "estimate" throughout the dashboard, so the confirmation prompt is reworded
 * to match. All other orgs keep the standard global BO wording.
 *
 * @param orgId - Firebase organization document ID (defaults to current facility).
 * @returns Prompt label for the BO/estimate confirmation dropdown.
 */
export const getBoConfirmationLabel = (orgId?: string): string => {
  const facilityId = orgId ?? getOrgIdForFetchExternalWorklist();
  return isExceptionFlowForUrologyOrg(facilityId)
    ? SUNSTATE_BO_CONFIRMATION_LABEL
    : DEFAULT_BO_CONFIRMATION_LABEL;
};

/**
 * Determines whether the reassignment checkbox should be visible.
 *
 * For orgs in `exceptionFlowForUrologyOrgs` the checkbox appears when auth
 * status is `auth_required`, `query`, or `query_resolved`.
 * For all other orgs it only appears for `auth_required`.
 *
 * @param authStatus - Current auth status of the order.
 * @param orgId - Firebase organization document ID (defaults to current facility).
 * @returns `true` when the reassignment checkbox should render.
 */
export const shouldShowReassignmentCheckbox = (
  authStatus: string | undefined,
  orgId?: string,
): boolean => {
  if (!authStatus) return false;

  const facilityId = orgId ?? getCurrentFacilityId() ?? "";
  const allowedStatuses = isExceptionFlowForUrologyOrg(facilityId)
    ? SUNSTATE_REASSIGNMENT_STATUSES
    : DEFAULT_REASSIGNMENT_STATUSES;

  return allowedStatuses.has(authStatus);
};
